<template>
  <div>
    <iq-card>
      <template #headerTitle>
        <h4 class="card-title text-primary mb-0">
          <strong>PMS Offline Activities</strong>
        </h4>
      </template>
      <template #body>
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center flex-wrap">
            <date-picker
              v-model="filters.year"
              type="year"
              value-type="YYYY"
              format="YYYY"
              placeholder="Select Year"
              class="mr-2 mb-2"
              style="width: 160px"
            />
            <v-multiselect
              v-model="filters.vehicleTypeIds"
              tag-placeholder="Fleet Type"
              placeholder="Select Fleet Type"
              label="name"
              track-by="code"
              :options="fleetCategoryOpt"
              :multiple="true"
              :taggable="true"
              class="mr-2 mb-2"
              style="width: 260px"
              @input="onVehicleTypeChange"
            />
            <v-multiselect
              v-model="filters.vehicleIds"
              tag-placeholder="Fleet"
              placeholder="Select Fleet"
              label="name"
              track-by="code"
              :options="fleetNameOpt"
              :multiple="true"
              :taggable="true"
              :loading="fleetLoading"
              class="mr-2 mb-2"
              style="width: 400px"
            />
            <b-button
              variant="outline-primary"
              class="mb-2 d-flex align-items-center text-nowrap"
              :disabled="tableLoading"
              @click="applyFilter"
            >
              <i class="fas fa-sliders mr-1"></i>
              Filter Data
            </b-button>
          </div>
        </div>

        <div v-if="initialLoading" class="text-center py-5">
          <b-spinner label="Loading..."></b-spinner>
        </div>
        <div v-else class="table-responsive offline-table-wrap">
          <table
            class="table table-bordered table-striped table-hover mb-0 offline-table"
            :class="{ 'table-loading': tableLoading }"
          >
            <thead>
              <tr>
                <th rowspan="2" class="text-white align-middle frozen-fleet-name">
                  FLEET NAME
                </th>
                <th
                  colspan="12"
                  class="text-white text-center year-header"
                >
                  {{ selectedYear }}
                </th>
              </tr>
              <tr>
                <th
                  v-for="month in months"
                  :key="month.value"
                  class="text-white text-center"
                >
                  {{ month.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!rows.length">
                <td colspan="13" class="text-center text-muted py-4">
                  No data available
                </td>
              </tr>
              <tr
                v-for="row in paginatedRows"
                :key="row.id || row.vehicle_id || row.name"
              >
                <td class="fleet-cell frozen-fleet-name">
                  <span class="fleet-badge">
                    {{ row.name || row.vehicle_name || '-' }}
                  </span>
                </td>
                <td
                  v-for="month in months"
                  :key="`${row.id || row.vehicle_id || row.name}-${month.value}`"
                  class="text-center"
                >
                  <b-dropdown
                    v-if="getMonthFile(row, month)"
                    size="sm"
                    variant="outline-primary"
                    text="Available"
                    class="activity-action"
                  >
                    <b-dropdown-item
                      @click="downloadFile(getMonthFile(row, month))"
                    >
                      Download
                    </b-dropdown-item>
                    <b-dropdown-item
                      @click="openOfflineActivityModal(row, month)"
                    >
                      Edit
                    </b-dropdown-item>
                  </b-dropdown>
                  <button
                    v-else
                    type="button"
                    class="not-available"
                    @click="openOfflineActivityModal(row, month)"
                  >
                    Not Available
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="!initialLoading"
          class="mt-4 d-flex align-items-center w-100 justify-content-between"
        >
          <div>
            <span class="mb-0">
              Total Rows :
              <strong>{{ totalRows }}</strong>
            </span>
          </div>
          <div class="d-flex align-items-center">
            <b-pagination
              class="mb-0 mr-3"
              v-model="filters.page"
              :total-rows="totalRows"
              :per-page="filters.limit"
              first-number
              align="center"
            ></b-pagination>
            <b-form-select
              plain
              v-model="filters.limit"
              :options="limitOptions"
              style="width: 140px"
              @input="onChangeLimit"
            ></b-form-select>
          </div>
        </div>
      </template>
    </iq-card>

    <b-modal
      v-model="offlineActivityModal.show"
      :title="offlineActivityModalTitle"
      hide-footer
      centered
      size="md"
      body-class="pb-3"
    >
      <b-form @submit.prevent="submitOfflineActivity">
        <b-form-input
          :value="offlineActivityModal.monthLabel"
          class="mb-3"
          readonly
        ></b-form-input>
        <b-form-textarea
          v-model="offlineActivityModal.url"
          rows="4"
          max-rows="6"
          class="mb-4"
          placeholder="Insert activity link..."
        ></b-form-textarea>
        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-primary"
            class="modal-action-btn mr-2"
            :disabled="offlineActivityModal.loading"
            @click="closeOfflineActivityModal"
          >
            Cancel & Close
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            class="modal-action-btn"
            :disabled="offlineActivityModal.loading"
          >
            <b-spinner
              v-if="offlineActivityModal.loading"
              small
              class="mr-1"
            ></b-spinner>
            Submit
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import moment from '@/node_modules/moment/moment'
import { limitOptions } from '@/src/core/mock/data/optionsData'
import { fleetsActions, pmsActions } from '@/src/Utils/helper'

export default {
  name: 'ReportPmsOffline',
  data () {
    return {
      initialLoading: true,
      tableLoading: false,
      fleetLoading: false,
      limitOptions,
      rows: [],
      offlineActivityModal: {
        show: false,
        loading: false,
        row: null,
        month: null,
        monthLabel: '',
        url: '',
        mode: 'create'
      },
      allFleetTypeOption: {
        name: 'All Fleet Type',
        code: '',
        isAll: true
      },
      filters: {
        year: moment().format('YYYY'),
        vehicleTypeIds: [
          {
            name: 'All Fleet Type',
            code: '',
            isAll: true
          }
        ],
        vehicleIds: [],
        limit: 10,
        page: 1
      },
      fleetCategoryOpt: [],
      fleetNameOpt: [],
      months: [
        { value: 'january', label: 'January', number: 1 },
        { value: 'february', label: 'February', number: 2 },
        { value: 'march', label: 'March', number: 3 },
        { value: 'april', label: 'April', number: 4 },
        { value: 'may', label: 'May', number: 5 },
        { value: 'june', label: 'June', number: 6 },
        { value: 'july', label: 'July', number: 7 },
        { value: 'august', label: 'August', number: 8 },
        { value: 'september', label: 'September', number: 9 },
        { value: 'october', label: 'October', number: 10 },
        { value: 'november', label: 'November', number: 11 },
        { value: 'december', label: 'December', number: 12 }
      ]
    }
  },
  computed: {
    selectedYear () {
      return this.filters.year || moment().format('YYYY')
    },
    totalRows () {
      return this.rows.length
    },
    paginatedRows () {
      const start = (this.filters.page - 1) * this.filters.limit
      return this.rows.slice(start, start + this.filters.limit)
    },
    offlineActivityModalTitle () {
      return this.offlineActivityModal.mode === 'edit'
        ? 'Edit Link Activity PMS Offline'
        : 'Insert Link Activity PMS Offline'
    }
  },
  methods: {
    ...pmsActions,
    ...fleetsActions,
    mappingIds (data) {
      return data?.map((el) => el.code)?.toString()
    },
    selectedVehicleTypeIds () {
      if (this.filters.vehicleTypeIds.some((type) => type.isAll)) return ''
      return this.mappingIds(this.filters.vehicleTypeIds)
    },
    buildPayload () {
      return {
        year: this.selectedYear,
        start_date: moment(this.selectedYear, 'YYYY')
          .startOf('year')
          .format('YYYY-MM-DD'),
        end_date: moment(this.selectedYear, 'YYYY')
          .endOf('year')
          .format('YYYY-MM-DD'),
        vehicle_type_id: this.selectedVehicleTypeIds(),
        vehicle_ids: this.mappingIds(this.filters.vehicleIds)
      }
    },
    normalizeRows (payload) {
      const data = payload?.data?.data || payload?.data || payload || []
      return Array.isArray(data) ? data : data?.data || []
    },
    async fetchData ({ silent = false } = {}) {
      if (silent) this.tableLoading = true
      else this.initialLoading = true

      try {
        const res = await this.getPmsOfflineActivities(this.buildPayload())
        if (res.status === 'success') {
          this.rows = this.normalizeRows(res.data)
        }
      } catch (error) {
        console.log('Error fetching PMS offline activities:', error)
      } finally {
        this.initialLoading = false
        this.tableLoading = false
      }
    },
    applyFilter () {
      this.filters.page = 1
      this.fetchData({ silent: true })
    },
    onChangeLimit () {
      this.filters.page = 1
    },
    getVehicleId (row) {
      return row?.vehicle_id || row?.vehicleId || row?.id
    },
    getFileUrl (file) {
      return file?.url || file?.fileurl || file?.file_url || file?.download_url || ''
    },
    openOfflineActivityModal (row, month) {
      const file = this.getMonthFile(row, month)
      this.offlineActivityModal = {
        show: true,
        loading: false,
        row,
        month,
        monthLabel: moment(
          `${this.selectedYear}-${month.number}-01`,
          'YYYY-M-DD'
        ).format('MMMM YYYY'),
        url: this.getFileUrl(file),
        mode: file ? 'edit' : 'create'
      }
    },
    closeOfflineActivityModal () {
      this.offlineActivityModal = {
        show: false,
        loading: false,
        row: null,
        month: null,
        monthLabel: '',
        url: '',
        mode: 'create'
      }
    },
    buildOfflineActivityPayload () {
      const selectedMonthNumber = Number(this.offlineActivityModal.month.number)
      const selectedUrl = (this.offlineActivityModal.url || '').trim()

      return this.months.map((month) => {
        const currentFile = this.getMonthFile(
          this.offlineActivityModal.row,
          month
        )
        const currentUrl = this.getFileUrl(currentFile)

        return {
          month: month.number,
          url:
            Number(month.number) === selectedMonthNumber
              ? selectedUrl
              : currentUrl
        }
      })
    },
    async submitOfflineActivity () {
      const payload = {
        vehicle_id: this.getVehicleId(this.offlineActivityModal.row),
        year: Number(this.selectedYear),
        activities: this.buildOfflineActivityPayload()
      }

      if (!payload.vehicle_id) {
        this.$swal('Error', 'Fleet data is invalid.', 'error')
        return
      }

      try {
        this.offlineActivityModal.loading = true
        const res = await this.createPmsOfflineActivity(payload)
        if (res.status === 'success') {
          this.$swal('Success', 'Activity link has been saved.', 'success')
          this.closeOfflineActivityModal()
          this.fetchData({ silent: true })
        } else {
          this.$swal('Error', 'Failed to save activity link.', 'error')
        }
      } catch (error) {
        console.log(error)
        this.$swal('Error', 'Failed to save activity link.', 'error')
      } finally {
        this.offlineActivityModal.loading = false
      }
    },
    async fetchFleetCategories () {
      try {
        this.fleetCategoryOpt = [this.allFleetTypeOption]
        const res = await this.getFleetTypes({ page: 1, perPage: 1000 })
        res.data.forEach((el) => {
          this.fleetCategoryOpt.push({
            name: el.name,
            code: el.id
          })
        })
      } catch (error) {
        console.log(error)
      }
    },
    async fetchFleets () {
      try {
        this.fleetLoading = true
        this.fleetNameOpt = []
        const res = await this.getFleets({
          page: 1,
          perPage: 1000,
          active: true,
          vehicleTypeId: this.selectedVehicleTypeIds(),
          vehicleTypeIds: this.selectedVehicleTypeIds()
        })
        res.data.forEach((el) => {
          this.fleetNameOpt.push({
            name: el.name,
            code: el.id
          })
        })
      } catch (error) {
        console.log(error)
      } finally {
        this.fleetLoading = false
      }
    },
    async onVehicleTypeChange () {
      if (this.filters.vehicleTypeIds.some((type) => type.isAll)) {
        this.filters.vehicleTypeIds = [this.allFleetTypeOption]
      }
      await this.fetchFleets()
      const validFleetIds = this.fleetNameOpt.map((fleet) => fleet.code)
      this.filters.vehicleIds = this.filters.vehicleIds.filter((fleet) =>
        validFleetIds.includes(fleet.code)
      )
    },
    getMonthFile (row, month) {
      const offlineActivity = row?.offline_activity?.activities?.find(
        (activity) => Number(activity?.month) === Number(month.number)
      )
      if (offlineActivity?.url) {
        return { url: offlineActivity.url }
      }

      const candidates = [
        row?.months?.[month.value],
        row?.months?.[month.label],
        row?.months?.[month.number],
        row?.activities?.[month.value],
        row?.activities?.[month.label],
        row?.activities?.[month.number],
        row?.[month.value],
        row?.[month.label]
      ]
      const file = candidates.find((item) => item)
      if (typeof file === 'string') {
        return { url: file }
      }
      if (
        file?.fileurl ||
        file?.file_url ||
        file?.url ||
        file?.download_url
      ) {
        return file
      }
      return null
    },
    downloadFile (file) {
      const url = this.getFileUrl(file)
      if (!url) return
      window.open(url, '_blank')
    }
  },
  mounted () {
    this.filters.vehicleTypeIds = [this.allFleetTypeOption]
    this.fetchData()
    this.fetchFleetCategories()
    this.fetchFleets()
  }
}
</script>

<style scoped>
.offline-table-wrap {
  border-bottom: 2px solid #f0ad1e;
}
.offline-table th {
  min-width: 100px;
  vertical-align: middle;
}
.offline-table thead th {
  background: #334697;
}
.offline-table th:first-child,
.offline-table td:first-child {
  min-width: 120px;
}
.frozen-fleet-name {
  position: sticky;
  left: 0;
  z-index: 3;
  background: #ffffff;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.08);
}
.offline-table thead .frozen-fleet-name {
  z-index: 4;
  background: #334697;
}
.offline-table tbody tr:nth-of-type(odd) .frozen-fleet-name {
  background: #f9f9f9;
}
.offline-table tbody tr:nth-of-type(even) .frozen-fleet-name {
  background: #ffffff;
}
.year-header {
  font-size: 14px;
}
.fleet-cell {
  width: 120px;
}
.fleet-badge {
  display: block;
  background: #7fd45a;
  color: #ffffff;
  font-weight: 700;
  border-radius: 4px;
  padding: 8px 12px;
  text-align: center;
  white-space: nowrap;
}
.download-btn {
  min-width: 76px;
  font-size: 12px;
}
.activity-action {
  min-width: 88px;
}
.not-available {
  display: inline-block;
  min-width: 88px;
  padding: 8px 10px;
  border: 0;
  border-radius: 4px;
  background: #e5e5e5;
  color: #333333;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
}
.not-available:hover {
  background: #d8d8d8;
}
.modal-action-btn {
  min-width: 128px;
}
.table-loading {
  opacity: 0.6;
  pointer-events: none;
}
</style>
